import { useState } from "react";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { BackupSchedule } from "../../types";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

interface ScheduleFormProps {
  serviceId: string;
  onCreated: (schedule: BackupSchedule) => void;
  onCancel: () => void;
}

const PRESET_CRONS: { label: string; value: string }[] = [
  { label: "Daily at 2am", value: "0 2 * * *" },
  { label: "Weekly (Sunday 2am)", value: "0 2 * * 0" },
  { label: "Every 6 hours", value: "0 */6 * * *" },
  { label: "Hourly", value: "0 * * * *" },
];

export function ScheduleForm({ serviceId, onCreated, onCancel }: ScheduleFormProps) {
  const [cron, setCron] = useState("0 2 * * *");
  const [enabled, setEnabled] = useState(true);
  const [retentionDays, setRetentionDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = cron.trim();
    if (!trimmed) {
      notify.error("Cron expression is required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.backups.schedules.create(serviceId, {
        type: "config",
        cronExpression: trimmed,
        enabled,
        retentionDays,
      });
      notify.success("Schedule created");
      onCreated(res.schedule);
    } catch (err) {
      handleError(err, { scope: "ScheduleForm" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-md bg-card p-4">
      <div className="space-y-1.5">
        <Label>Cron expression (5 fields)</Label>
        <div className="flex gap-2 flex-wrap">
          {PRESET_CRONS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setCron(p.value)}
              className={`rounded border px-2 py-0.5 text-xs transition-colors ${
                cron === p.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Input
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          placeholder="0 2 * * *"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">Format: minute hour day month weekday</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="retention">Retention (days)</Label>
        <Input
          id="retention"
          type="number"
          min={1}
          max={365}
          value={retentionDays}
          onChange={(e) => setRetentionDays(Number(e.target.value))}
          className="w-24"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch id="sched-enabled" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="sched-enabled">Enable schedule</Label>
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? "Saving…" : "Create schedule"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
